/**
 * 统计卡片组件 — 标题 + 数值 + 趋势 + 图标。
 * 用于运营看板顶部 KPI 展示。
 */
import { Card, CardContent, Box, Typography } from "@mui/material";
import type { ReactNode } from "react";

export interface StatCardProps {
  /** 卡片标题。 */
  title: string;
  /** 主数值。 */
  value: string | number;
  /** 数值后缀（如 ¥、%）。 */
  suffix?: string;
  /** 副标题/说明文字。 */
  subtitle?: string;
  /** 趋势数值（正数表示增长，负数表示下降）。 */
  trend?: number;
  /** 图标节点。 */
  icon?: ReactNode;
  /** 图标背景色。 */
  iconBg?: string;
  /** 图标颜色。 */
  iconColor?: string;
}

export default function StatCard({
  title,
  value,
  suffix,
  subtitle,
  trend,
  icon,
  iconBg = "brand.50",
  iconColor = "primary.main",
}: StatCardProps) {
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <Box>
            <Typography
              sx={{ fontSize: 12, color: "text.secondary", mb: 0.5 }}
            >
              {title}
            </Typography>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
              <Typography
                sx={{ fontSize: 26, fontWeight: 700, color: "text.primary" }}
              >
                {value}
              </Typography>
              {suffix && (
                <Typography sx={{ fontSize: 14, color: "text.secondary" }}>
                  {suffix}
                </Typography>
              )}
            </Box>
            {subtitle && (
              <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>
                {subtitle}
              </Typography>
            )}
            {trend !== undefined && (
              <Typography
                sx={{
                  fontSize: 11,
                  mt: 0.5,
                  color: trend >= 0 ? "success.main" : "error.main",
                }}
              >
                {trend >= 0 ? "↑" : "↓"} {Math.abs(trend)}
              </Typography>
            )}
          </Box>
          {icon && (
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: iconBg,
                color: iconColor,
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}
